import { Link } from "react-router";
import i18n, { type TFunction } from "i18next";
import {
  type Column,
  type ColumnDef,
  type SortFn,
  type Table as TableInstance,
} from "@tanstack/react-table";
import { ArrowUpDown } from "lucide-react";
import type { DocumentSummary } from "~/api/types";
import { Button } from "~/atoms/button";
import { formatDate } from "~/helpers/date";
import { printFullName } from "~/helpers/user";
import AvatarWithFallback from "~/molecules/AvatarWithFallback";
import ItemsTooltip from "~/molecules/ItemsTooltip";
import MoreTooltip from "~/molecules/MoreTooltip";
import { TagsValue } from "~/molecules/Tags";
import type { EditableField } from "~/helpers/libraryTypes";
import { getNoTierLabel, getTierTitle } from "~/helpers/tier";
import type { DataTableFeatures } from "~/molecules/dataTableFeatures";
import { getReviewPeriodLabel, reviewPeriodInMonths } from "~/helpers/document";

type DocumentsColumnId =
  | "title"
  | "tier"
  | "category"
  | "referenceNumber"
  | "tag"
  | "owner"
  | "publisher"
  | "links"
  | "publishedAt"
  | "adoptedAt"
  | "scheduleReviewPeriod"
  | "reviewFrom"
  | "reviewTo";

type DocumentsColumnGroupId =
  "metadata" | "latestPublishedRelease" | "nextReview";
type DocumentsFeatureFlag = "referenceNumber";

interface DocumentsColumnSpec {
  id: DocumentsColumnId;
  getLabel: (t: TFunction) => string;
  size: number;
  group?: DocumentsColumnGroupId;
  editableField?: EditableField;
  featureFlag?: DocumentsFeatureFlag;
}

interface CreateDocumentsColumnsParams {
  t: TFunction;
  currentUserId: number;
  filteredTagIds: string[];
  columnSpecs: DocumentsColumnSpec[];
}

interface ResolveDocumentsColumnMenuParams {
  columnSpecs: DocumentsColumnSpec[];
  table: TableInstance<DataTableFeatures, DocumentSummary>;
  t: TFunction;
}

interface ColumnBuilderContext {
  currentUserId: number;
  spec: DocumentsColumnSpec;
  t: TFunction;
  filteredTagIds: Set<string>;
}

export interface ResolvedDocumentsColumnGroup {
  id: string;
  label: string;
  columns: Column<DataTableFeatures, DocumentSummary, unknown>[];
}

const columnGroupLabelById: Record<
  DocumentsColumnGroupId,
  (t: TFunction) => string
> = {
  metadata: (t) => t(($) => $.commons.document.metadata),
  latestPublishedRelease: (t) =>
    t(($) => $.commons.document.latestPublishedVersion),
  nextReview: (t) => t(($) => $.commons.workflow.nextReviewPeriod),
};

const documentsColumnSpecs: DocumentsColumnSpec[] = [
  {
    id: "referenceNumber",
    getLabel: (t) => t(($) => $.commons.document.referenceNumber),
    size: 140,
    group: "metadata",
    editableField: "referenceNumber",
    featureFlag: "referenceNumber",
  },
  {
    id: "title",
    getLabel: (t) => t(($) => $.commons.document.title),
    size: 320,
  },
  {
    id: "tier",
    getLabel: (t) => t(($) => $.commons.document.tier),
    size: 160,
    group: "metadata",
    editableField: "tierId",
  },
  {
    id: "category",
    getLabel: (t) => t(($) => $.commons.document.category),
    size: 160,
    group: "metadata",
    editableField: "categoryId",
  },
  {
    id: "tag",
    getLabel: (t) => t(($) => $.commons.document.tags),
    size: 180,
    group: "metadata",
    editableField: "tags",
  },
  {
    id: "owner",
    getLabel: (t) => t(($) => $.commons.document.owner),
    size: 100,
    group: "metadata",
    editableField: "ownerId",
  },
  {
    id: "publisher",
    getLabel: (t) => t(($) => $.commons.document.publisher),
    size: 100,
    group: "metadata",
    editableField: "publisherId",
  },
  {
    id: "links",
    getLabel: (t) => t(($) => $.commons.document.links),
    size: 130,
    group: "metadata",
    editableField: "links",
  },
  {
    id: "scheduleReviewPeriod",
    getLabel: (t) => t(($) => $.commons.document.recurringReview),
    size: 180,
    group: "metadata",
    editableField: "scheduleReviewPeriod",
  },
  {
    id: "publishedAt",
    getLabel: (t) => t(($) => $.commons.document.publishedAt),
    size: 130,
    group: "latestPublishedRelease",
  },
  {
    id: "adoptedAt",
    getLabel: (t) => t(($) => $.commons.document.adoptedAt),
    size: 130,
    group: "latestPublishedRelease",
  },
  {
    id: "reviewFrom",
    getLabel: (t) => t(($) => $.commons.document.reviewFrom),
    size: 130,
    group: "nextReview",
    editableField: "reviewFrom",
  },
  {
    id: "reviewTo",
    getLabel: (t) => t(($) => $.commons.document.reviewTo),
    size: 130,
    group: "nextReview",
    editableField: "reviewTo",
  },
] as const;

const MIN_COLUMN_SIZE = 80;

const editableFieldLabelByField = Object.fromEntries(
  documentsColumnSpecs.flatMap((spec) =>
    spec.editableField ? [[spec.editableField, spec.getLabel]] : [],
  ),
) as Record<EditableField, (t: TFunction) => string>;

const naturalSort: SortFn<DataTableFeatures, DocumentSummary> = (
  rowA,
  rowB,
  columnId,
) =>
  new Intl.Collator(i18n.language, { numeric: true }).compare(
    String(rowA.getValue(columnId) ?? ""),
    String(rowB.getValue(columnId) ?? ""),
  );

const SortableColumnHeader = ({
  column,
  label,
}: {
  column: Column<DataTableFeatures, DocumentSummary, unknown>;
  label: string;
}) => (
  <Button
    variant="ghost"
    className="h-auto p-0 hover:bg-transparent hover:underline"
    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
  >
    {label}
    {column.getIsSorted() === "asc" ? (
      "↑"
    ) : column.getIsSorted() === "desc" ? (
      "↓"
    ) : (
      <ArrowUpDown className="size-4 opacity-50" />
    )}
  </Button>
);

const sortableHeader: ColumnDef<
  DataTableFeatures,
  DocumentSummary
>["header"] = ({ column }) => (
  <SortableColumnHeader
    column={column}
    label={column.columnDef.meta?.label ?? ""}
  />
);

const columnBuilders: Record<
  DocumentsColumnId,
  (
    context: ColumnBuilderContext,
  ) => ColumnDef<DataTableFeatures, DocumentSummary>
> = {
  title: ({ spec, t }) => ({
    id: spec.id,
    accessorFn: (row) => row.name,
    sortFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    cell: ({ row }) => (
      <Link
        to={`/documents/${row.original.id}?releaseId=${(row.original.latestPublishedRelease ?? row.original.latestRelease).id}&tab=metadata`}
        className="hover:underline"
        title={row.original.name}
      >
        {row.original.name}
      </Link>
    ),
  }),
  tier: ({ spec, t }) => ({
    id: spec.id,
    sortFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => getTierTitle(row.tier?.title, t),
    cell: ({ row }) =>
      row.original.tier ? (
        <span title={row.original.tier.title}>{row.original.tier.title}</span>
      ) : (
        <span className="text-muted-foreground">{getNoTierLabel(t)}</span>
      ),
  }),
  category: ({ spec, t }) => ({
    id: spec.id,
    sortFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => row.category.title,
    cell: ({ row }) => (
      <span title={row.original.category.title}>
        {row.original.category.title}
      </span>
    ),
  }),
  referenceNumber: ({ spec, t }) => ({
    id: spec.id,
    sortFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => row.referenceNumber ?? "",
    cell: ({ row }) =>
      row.original.referenceNumber ? (
        <span title={row.original.referenceNumber}>
          {row.original.referenceNumber}
        </span>
      ) : (
        "-"
      ),
  }),
  tag: ({ spec, t, filteredTagIds }) => ({
    id: spec.id,
    sortFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => {
      const titles = row.tags.map((tag) => tag.title).sort();
      return titles.length > 0 ? titles.join(", ") : undefined;
    },
    sortUndefined: "last",
    cell: ({ row }) => {
      const docTags = [...row.original.tags].sort((a, b) => {
        const aMatch = filteredTagIds.has(a.id.toString());
        const bMatch = filteredTagIds.has(b.id.toString());
        if (aMatch !== bMatch) return aMatch ? -1 : 1;
        return a.title.localeCompare(b.title);
      });

      return (
        <div className="flex items-center gap-1">
          {docTags.length > 0 ? (
            <ItemsTooltip items={[docTags[0]!.title]} variant="tag">
              <TagsValue className="min-w-8 shrink">
                <span className="truncate">{docTags[0]!.title}</span>
              </TagsValue>
            </ItemsTooltip>
          ) : (
            "-"
          )}
          <MoreTooltip data={docTags.map((tag) => tag.title)} variant="tag" />
        </div>
      );
    },
  }),
  owner: ({ spec, t }) => ({
    id: spec.id,
    sortFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => printFullName(row.owner),
    cell: ({ row }) => (
      <AvatarWithFallback user={row.original.owner} size="xs" showTooltip />
    ),
  }),
  publisher: ({ spec, t }) => ({
    id: spec.id,
    sortFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => printFullName(row.publisher),
    cell: ({ row }) =>
      row.original.publisher ? (
        <AvatarWithFallback
          user={row.original.publisher}
          size="xs"
          showTooltip
        />
      ) : (
        "-"
      ),
  }),
  links: ({ spec, t }) => ({
    id: spec.id,
    sortFn: "basic",
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => row.linkedDocumentIds.length,
    cell: ({ row }) => {
      const totalLinks = row.original.linkedDocumentIds.length;
      return totalLinks > 0 ? totalLinks : "-";
    },
  }),
  publishedAt: ({ spec, t }) => ({
    id: spec.id,
    accessorFn: (row) =>
      row.latestPublishedRelease?.artifact.publishedAt ?? undefined,
    sortFn: "datetime",
    sortUndefined: "last",
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    cell: ({ row }) =>
      formatDate(
        row.original.latestPublishedRelease?.artifact.publishedAt ?? null,
      ),
  }),
  adoptedAt: ({ spec, t }) => ({
    id: spec.id,
    accessorFn: (row) => row.latestPublishedRelease?.adoptedAt ?? undefined,
    sortFn: "datetime",
    sortUndefined: "last",
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    cell: ({ row }) =>
      formatDate(row.original.latestPublishedRelease?.adoptedAt ?? null),
  }),
  scheduleReviewPeriod: ({ spec, t }) => ({
    id: spec.id,
    accessorFn: (row) =>
      row.scheduleReviewPeriod
        ? reviewPeriodInMonths[row.scheduleReviewPeriod]
        : undefined,
    sortFn: "basic",
    sortUndefined: "last",
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    cell: ({ row }) =>
      getReviewPeriodLabel(row.original.scheduleReviewPeriod, t) ?? "-",
  }),
  reviewFrom: ({ spec, t }) => ({
    id: spec.id,
    accessorFn: (row) => row.latestRelease.artifact.reviewFrom ?? undefined,
    sortFn: "datetime",
    sortUndefined: "last",
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    cell: ({ row }) =>
      formatDate(row.original.latestRelease.artifact.reviewFrom),
  }),
  reviewTo: ({ spec, t }) => ({
    id: spec.id,
    accessorFn: (row) => row.latestRelease.artifact.reviewTo ?? undefined,
    sortFn: "datetime",
    sortUndefined: "last",
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    cell: ({ row }) => formatDate(row.original.latestRelease.artifact.reviewTo),
  }),
};

export const getDocumentsListColumnSpecs = (
  enabledFeatureFlags: Record<DocumentsFeatureFlag, boolean>,
): DocumentsColumnSpec[] =>
  documentsColumnSpecs.filter(
    (spec) => !spec.featureFlag || enabledFeatureFlags[spec.featureFlag],
  );

export const createDocumentsColumns = ({
  t,
  currentUserId,
  filteredTagIds,
  columnSpecs,
}: CreateDocumentsColumnsParams): ColumnDef<
  DataTableFeatures,
  DocumentSummary
>[] => {
  const filteredTagIdSet = new Set(filteredTagIds);
  return columnSpecs.map((spec) => ({
    ...columnBuilders[spec.id]({
      currentUserId,
      spec,
      t,
      filteredTagIds: filteredTagIdSet,
    }),
    size: spec.size,
    minSize: MIN_COLUMN_SIZE,
  }));
};

export const getEditableFieldByColumnId = (
  columnSpecs: DocumentsColumnSpec[],
): Partial<Record<string, EditableField>> =>
  Object.fromEntries(
    columnSpecs.flatMap((spec) =>
      spec.editableField ? [[spec.id, spec.editableField]] : [],
    ),
  );

export const getEditableFieldLabel = (
  field: EditableField,
  t: TFunction,
): string => {
  const getLabel = editableFieldLabelByField[field];
  if (!getLabel) {
    throw new Error(`Missing label for editable field: ${field}`);
  }

  return getLabel(t);
};

export const resolveDocumentsColumnMenu = ({
  columnSpecs,
  table,
  t,
}: ResolveDocumentsColumnMenuParams): {
  columnMenuLabels: Record<string, string>;
  resolvedGroups: ResolvedDocumentsColumnGroup[];
  ungroupedColumns: Column<DataTableFeatures, DocumentSummary, unknown>[];
} => {
  const columnMenuLabels = Object.fromEntries(
    columnSpecs.map((spec) => [spec.id, spec.getLabel(t)]),
  );
  const hideableColumns = table
    .getAllColumns()
    .filter((column) => column.getCanHide());
  const groupedSpecs = (
    Object.keys(columnGroupLabelById) as DocumentsColumnGroupId[]
  ).map((groupId) => {
    const groupColumnIds = columnSpecs
      .filter((spec) => spec.group === groupId)
      .map((spec) => spec.id);

    return {
      id: groupId,
      label: columnGroupLabelById[groupId](t),
      columnIds: groupColumnIds,
    };
  });
  const groupedColumnIds = new Set<string>(
    groupedSpecs.flatMap((group) => group.columnIds),
  );
  const ungroupedColumns = hideableColumns.filter(
    (column) => !groupedColumnIds.has(column.id),
  );
  const resolvedGroups = groupedSpecs
    .map((group) => ({
      ...group,
      columns: group.columnIds
        .map((columnId) => table.getColumn(columnId))
        .filter((column): column is NonNullable<typeof column> => !!column),
    }))
    .filter((group) => group.columns.length > 0);

  return {
    columnMenuLabels,
    resolvedGroups,
    ungroupedColumns,
  };
};
